// The tile panel of PRD section 19.5, only as far as entering a tile (H2): the tile's name, terrain
// and size, what goes in and comes out per minute at its doors, how hard its machines work, and
// «Entrar». A side panel on the right in landscape and a bottom sheet in portrait, as in the design
// system's kit; it closes with its button or by touching the map elsewhere.
import type { GameData } from '../sim/data';
import type { TileReport } from '../sim/report';
import { button, progressBar, squircle } from './controls';
import { h, icon } from './dom';
import { percent, perMinute } from './figures';
import { ICONS } from './icons';
import { itemIcon } from './item-icon';

export interface TilePanelHandlers {
  readonly onEnter: () => void;
  readonly onClose: () => void;
}

export class TilePanel {
  readonly el: HTMLElement;
  private readonly title = h('span', { class: 'panel__title-text' });
  private readonly subtitle = h('small', {});
  private readonly rows = h('div', { class: 'tile-panel__rows' });
  private readonly work = progressBar();
  private readonly workRow: HTMLElement;
  private readonly notice = h('div', { class: 'soft-notice soft-notice--info', hidden: true });
  private tileId: string | undefined;

  constructor(
    private readonly data: GameData,
    handlers: TilePanelHandlers,
  ) {
    const close = h('button', { type: 'button', class: 'icon-button', 'aria-label': 'Cerrar' }, icon(ICONS.close));
    close.addEventListener('click', handlers.onClose);
    this.workRow = h('div', { class: 'tile-panel__work' }, h('span', { class: 'tile-panel__work-label' }, 'Trabajo'), this.work.el);
    this.el = h(
      'section',
      { class: 'panel tile-panel', role: 'dialog', 'aria-label': 'Casilla', hidden: true },
      h('div', { class: 'tile-panel__grip', 'aria-hidden': 'true' }),
      h('header', { class: 'panel__header' }, squircle(icon(ICONS.cube, 'icon tile-panel__cube'), { size: 46 }), h('div', { class: 'panel__title' }, this.title, this.subtitle), close),
      h('div', { class: 'panel__body' }, this.rows, this.workRow, this.notice),
      h('footer', { class: 'panel__footer' }, button({ label: 'Entrar', icon: ICONS.signIn, variant: 'primary', size: 'lg', onClick: handlers.onEnter })),
    );
  }

  /** The tile shown, if the panel is open. */
  get shown(): string | undefined {
    return this.tileId;
  }

  show(tile: TileReport): void {
    this.tileId = tile.id;
    this.el.hidden = false;
    this.update(tile);
  }

  update(tile: TileReport): void {
    if (tile.id !== this.tileId) return;
    this.title.textContent = tile.name;
    const size = tile.interior ? ` · ${tile.interior.size.join('×')}` : '';
    this.subtitle.textContent = `${tile.terrain}${size}`;
    const itemName = (id: string): string => this.data.items.get(id)?.name ?? id;
    this.rows.replaceChildren(
      ...tile.ports.map((port) => {
        const item = port.stock[0]?.item;
        const rate = port.kind === 'in' ? port.outRate : port.inRate;
        const label = `${port.kind === 'in' ? 'Entra' : 'Sale'}${item ? ` · ${itemName(item)}` : ''}`;
        return h(
          'div',
          { class: 'stat-row', 'data-port': port.id },
          h('span', { class: 'stat-row__icon' }, item ? itemIcon(item, 24) : icon(port.kind === 'in' ? ICONS.signIn : ICONS.fastForward, `icon stat-row__door stat-row__door--${port.kind}`)),
          h('span', { class: 'stat-row__label' }, label),
          h('span', { class: 'stat-row__value' }, perMinute(rate)),
        );
      }),
    );
    const machines = tile.interior?.machines ?? [];
    const work = machines.length ? machines.reduce((s, m) => s + m.work, 0) / machines.length : 0;
    this.workRow.hidden = machines.length === 0;
    this.work.set(work, percent(work), work < 0.7 ? 'amber' : 'light');
    const waiting = tile.ports.find((p) => p.kind === 'in' && p.full && p.outRate < 1e-9);
    const text = machines.length === 0 ? 'Aún no hay máquinas. Entra para montar la cadena con el dedo.' : waiting ? 'La entrada está llena: dentro no se usa lo que llega.' : undefined;
    this.notice.hidden = !text;
    if (text) this.notice.replaceChildren(icon(ICONS.info, 'icon soft-notice__icon'), h('span', {}, text));
  }

  hide(): void {
    this.tileId = undefined;
    this.el.hidden = true;
  }
}
